import { Component, EventEmitter, OnInit, Output, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, FormArray, FormControl, Validators } from '@angular/forms';
import { Modifier, ModifierOption } from '@teem/app/core/model-forms/modifier-model';
import { ModifierService } from '@teem/app/core/services/modifier.service';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { LoaderService } from '@teem/app/core/services/loader.service';
import Swal from 'sweetalert2';
import { messages as i18n } from '@teem/app/core/constants/messages';

@Component({
  selector: 'app-modifier',
  templateUrl: './modifier.component.html',
  styleUrls: ['./modifier.component.scss']
})
export class ModifierComponent implements OnInit {
  modifiers: Modifier[] = [];
  modifierForm: FormGroup;
  editMode = false;
  currentId: number | null = null;
  optionsAtEdit: ModifierOption[] = [];

  @ViewChild('modifierModal') modalTemplate: any;
  @Output() notifyParent: EventEmitter<void> = new EventEmitter();

  constructor(
    private fb: FormBuilder,
    private modifierService: ModifierService,
    private modalService: NgbModal,
    private loaderService: LoaderService
  ) {
    this.modifierForm = this.fb.group({
      name: ['',Validators.required],
      name_ar: [''],
      modifier_options: this.fb.array([])
    });
  }

  get modifier_options(): FormArray {
    return this.modifierForm.get('modifier_options') as FormArray;
  }

  ngOnInit() {
    this.loadModifiers();
  }

  loadModifiers() {
    this.modifierService.getModifiers().subscribe(data => {
      this.modifiers = data;
    });
  }

  openModal(modifier: Modifier | null = null) {
    this.resetForm();

    if (modifier) {
      this.loaderService.startLoading();
      this.optionsAtEdit = modifier.modifier_options || [];
      this.editMode = true;
      this.currentId = modifier.id;
      this.modifierForm.patchValue({ name: modifier.name ,name_ar: modifier.name_ar});
      modifier.modifier_options.forEach(opt => {
        this.modifier_options.push(this.fb.group({
          name: [opt.name,Validators.required],
          name_ar: [opt.name_ar],
          price: [opt.price,[Validators.required, Validators.min(0)]]
        }));
      });
      this.loaderService.stopLoading();
    } else {
      this.editMode = false;
      this.currentId = null;
      this.addOption(); // start with one empty option
    }

    this.modalService.open(this.modalTemplate, { centered: true, windowClass: 'addons-modal', size: 'lg' });
  }

  addOption(option: any = null) {
    this.modifier_options.push(this.fb.group({
      name: [option?.name || '',Validators.required],
      name_ar: [option?.name_ar || ''],
      price: [option?.price || '',[Validators.required, Validators.min(0)]]
    }));
  }

  removeOption(index: number) {
    this.modifier_options.removeAt(index);
  }

  onDelete(id: number) {
    Swal.fire({
      title: i18n.delete,
      text: i18n.are_you_sure,
      icon: 'warning',
    }).then((willreload) => {
      if (willreload.value) {
        this.modifierService.deleteModifier(id).subscribe(() => this.loadModifiers());
      }
    });
  }

  onSave(modal: any) {
    if (this.modifierForm.invalid) {
      this.modifierForm.markAllAsTouched(); // triggers validation messages
      return;
    }
    this.loaderService.startLoading();
    const formValue = this.modifierForm.value;

    const modifier: Modifier = {
      id: this.currentId || undefined,
      name: formValue.name,
      name_ar: formValue.name_ar,
      modifier_options: formValue.modifier_options.map((opt: any, i: number) => ({
        id: this.editMode && this.optionsAtEdit[i]?.id, // preserve id if editing
        modifierId: this.currentId || undefined,
        name: opt.name,
        name_ar: opt.name_ar,
        price: opt.price
      })),
      minLimit:0,
      maxLimit:formValue.maxLimit
    };

    const request$ = this.editMode
      ? this.modifierService.updateModifier(modifier)
      : this.modifierService.addModifier(modifier);

    request$.subscribe(() => {
      this.loadModifiers();
      modal.close();
      this.loaderService.stopLoading();
    });
  }

  backToProduct(){
    this.notifyParent.emit();
  }


  private resetForm() {
    this.modifierForm.reset();
    this.modifier_options.clear();
  }
}